import React from 'react';
import { motion } from 'framer-motion';
import { Gallery } from './Gallery';
import { useLanguage } from './i18n/LanguageContext';
const fadeUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7,
    ease: [0.22, 1, 0.36, 1] as const
  }
};
export function Intro() {
  const {
    t
  } = useLanguage();
  return <section className="max-w-2xl mx-auto px-6 pt-16" data-id="element-200" data-mp="1srvmvq4g8vmi">
      <motion.div {...fadeUp} className="relative mb-11 bg-cream p-2 sm:p-3 overflow-hidden" data-id="element-201" data-mp="1nsr9soeemdqw">
        <span aria-hidden className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-serif text-[240px] sm:text-[320px] leading-none text-gold/[0.06] select-none" data-id="element-202" data-mp="22hni8mkdhss1">G</span>
        <div className="relative border border-gold/40 px-6 py-10 sm:px-12 sm:py-12 text-center" data-id="element-203" data-mp="1a0kwda2mykv5">
          <span aria-hidden className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rotate-45 bg-gold" data-id="element-204" data-mp="15218330kkwzm" />
          <div className="flex items-center justify-center gap-3 mb-5" aria-hidden data-id="element-205" data-mp="1r53aghclv6n">
            <span className="w-8 sm:w-14 h-px bg-gold-light" data-id="element-206" data-mp="6owvlblwnjbd" />
            <span className="w-1.5 h-1.5 rounded-full bg-gold" data-id="element-207" data-mp="6nc5by6mimjb" />
            <span className="w-8 sm:w-14 h-px bg-gold-light" data-id="element-208" data-mp="bhg8s7gokcfn" />
          </div>
          <p className="font-body font-semibold text-[10px] sm:text-[11px] tracking-[4px] sm:tracking-[6px] uppercase text-gold mb-6" data-id="element-209" data-mp="fi06bc561i2b">
            {t.intro.eyebrow}
          </p>
          <p className="font-serif font-light text-dark text-[34px] sm:text-[48px] leading-[1.02] tracking-[-1px]" data-id="element-210" data-mp="3jvovx00fob0f">
            {t.intro.titleTop}
            <br data-id="element-211" data-mp="3nw0fsakz27au" />
            <span className="italic text-gold" data-id="element-212" data-mp="3nzoahd25kzr5">{t.intro.titleBottom}</span>
          </p>
          <div className="flex items-center justify-center gap-3 my-6" aria-hidden data-id="element-213" data-mp="3sxy61orsi2da">
            <span className="w-12 sm:w-20 h-px bg-gold-ultra-light" data-id="element-214" data-mp="136jfruzyc0vz" />
            <span className="w-1 h-1 rounded-full bg-gold" data-id="element-215" data-mp="185waf1x2ec94" />
            <span className="w-12 sm:w-20 h-px bg-gold-ultra-light" data-id="element-216" data-mp="188igex4b4jp8" />
          </div>
          <p className="font-serif font-light italic text-mid text-[22px] sm:text-[30px] leading-none" data-id="element-217" data-mp="347r3dr9etibz">
            {t.intro.subTop}
            <br data-id="element-218" data-mp="3e7hx3qq5btb0" />
            <span className="not-italic text-gold" data-id="element-219" data-mp="3e89qcuw9tey4">{t.intro.subBottom}</span>
          </p>
        </div>
      </motion.div>

      <motion.p {...fadeUp} className="font-body font-light text-mid text-sm sm:text-base leading-[1.85] text-center" data-id="element-220" data-mp="399ykswd0dqlp">
        {t.intro.leadLine1}
        <br data-id="element-221" data-mp="2gjzqalg1p19n" />
        {t.intro.leadLine2}
        <strong className="font-semibold text-dark" data-id="element-222" data-mp="2gl106mlbrhps">{t.intro.leadStrong}</strong>
        <br data-id="element-223" data-mp="2v9e2s2ex3yt9" />
        {t.intro.leadLine3}
      </motion.p>

      {/* PAIN BLOCK */}
      <motion.div {...fadeUp} className="relative my-10 border border-gold/35 border-l-[4px] border-l-gold px-7 py-8 sm:px-9 overflow-hidden bg-[url('https://cdn.magicpatterns.com/patterns/generated-images/b3ba714e-490c-48d3-9920-7b397fa81ab1.jpg')] bg-cover bg-center" data-id="element-224" data-mp="2qifhspizicjo">
        <span aria-hidden className="absolute inset-0 bg-warm-white/80" data-id="element-225" data-mp="1xrdws7sm1ms8" />
        <span aria-hidden className="absolute top-5 right-6 font-serif text-6xl leading-none text-gold/[0.12] select-none" data-id="element-226" data-mp="dao15kv0ifew">&ldquo;</span>
        <div className="relative" data-id="element-227" data-mp="i40ozsrvai6l">
          <p className="font-body font-medium text-[10px] tracking-[4px] uppercase text-gold mb-4" data-id="element-228" data-mp="4bkg4bcabpxr">
            {t.intro.painEyebrow}
          </p>
          <p className="font-body font-light text-mid text-sm leading-[1.85]" data-id="element-229" data-mp="8aa1aq3kebry">
            {t.intro.painQuestions1}
            <strong className="font-semibold text-dark" data-id="element-230" data-mp="1111sta9oqc8y">{t.intro.painQuestionsStrong}</strong>
            {t.intro.painQuestions2}
          </p>
          <div className="w-12 h-px bg-gold/60 my-5" aria-hidden data-id="element-231" data-mp="n07kermn0baa" />
          <p className="font-body font-light text-mid text-sm leading-[1.85]" data-id="element-232" data-mp="s03oeqvc42pu">
            {t.intro.painBody}
          </p>
          <p className="font-body font-medium text-dark text-sm leading-[1.85] mt-4" data-id="element-233" data-mp="37r883ivn7v0y">
            {t.intro.painClose}
          </p>
        </div>
      </motion.div>

      <Gallery data-id="element-234" data-mp="3s07v41fny4ut" />

      {/* PROMISE BOX */}
      <motion.div {...fadeUp} className="relative my-10 bg-cream p-2 sm:p-3 overflow-hidden border border-gold-ultra-light" data-id="element-235" data-mp="3s19ect6u1xnm">
        <span aria-hidden className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-serif text-[260px] sm:text-[360px] leading-none text-gold/[0.06] select-none" data-id="element-236" data-mp="3n86mcphpew9j">
          G
        </span>
        <div className="relative border border-gold/45 px-7 py-11 sm:px-12 sm:py-14 text-center" data-id="element-237" data-mp="etpjzyecvgae">
          <span aria-hidden className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rotate-45 bg-gold" data-id="element-238" data-mp="9ulcrdazrsmp" />
          <div className="flex items-center justify-center gap-3 mb-6" aria-hidden data-id="element-239" data-mp="5s89qwnmq8tm">
            <span className="w-10 sm:w-16 h-px bg-gold-light" data-id="element-240" data-mp="u6nk2uwptdx" />
            <span className="w-1.5 h-1.5 rounded-full bg-gold" data-id="element-241" data-mp="w11uyboan91" />
            <span className="w-10 sm:w-16 h-px bg-gold-light" data-id="element-242" data-mp="2qka4lhaj5uwq" />
          </div>
          <p className="font-body font-semibold text-[10px] tracking-[4px] uppercase text-gold mb-6" data-id="element-243" data-mp="2ljma2ln3evxr">
            {t.intro.promiseEyebrow}
          </p>
          <p className="font-serif font-light text-dark text-[30px] sm:text-[40px] leading-[1.15] mb-7" data-id="element-244" data-mp="1100fq85tovux">
            {t.intro.promiseTitleTop}
            <br data-id="element-245" data-mp="1a29rf1ctuv9w" />
            <em className="italic text-gold" data-id="element-246" data-mp="1a1qfq2drt34b">{t.intro.promiseTitleBottom}</em>
          </p>
          <div className="flex items-center justify-center gap-3 mb-7" aria-hidden data-id="element-247" data-mp="1etrt5rmoczvx">
            <span className="w-12 sm:w-20 h-px bg-gold-ultra-light" data-id="element-248" data-mp="1jt4da4m5vsut" />
            <span className="w-1 h-1 rounded-full bg-gold" data-id="element-249" data-mp="1juyvhb61t6v7" />
            <span className="w-12 sm:w-20 h-px bg-gold-ultra-light" data-id="element-250" data-mp="1oqecgbzcrivu" />
          </div>
          <p className="font-body font-light text-mid text-sm leading-[1.9] max-w-md mx-auto" data-id="element-251" data-mp="1trtwq1tphld6">
            {t.intro.promiseBody1}
            <span className="font-medium text-dark" data-id="element-252" data-mp="1xrkr0fifyjmg">{t.intro.promiseBodyStrong}</span>
            {t.intro.promiseBody2}
            <em className="font-serif text-lg italic text-gold" data-id="element-253" data-mp="3vkhys7um62rd">{t.intro.promiseEm}</em>
            {t.intro.promiseBody3}
          </p>
        </div>
      </motion.div>
    </section>;
}